/**
 * Dynamic mapping between clean URL category slugs and search query parameters.
 *
 * /s/art/:category      → meta_collection=:category
 * /s/judaica/:category   → pub_category_j=has_all::category
 *
 * No hardcoded slug list — any category value works automatically.
 * The URL slug is always the plain value (e.g. "mezuzas").
 * For judaica, the API param is prefixed with "has_all:" automatically.
 */

const paramByListingType = {
  art: 'meta_collection',
  judaica: 'pub_category_j',
};

const JUDAICA_PREFIX = 'has_all:';

/**
 * Strip the "has_all:" prefix from a judaica param value to get the plain slug.
 */
const toPlainValue = (listingType, value) => {
  if (listingType === 'judaica' && value?.startsWith(JUDAICA_PREFIX)) {
    return value.slice(JUDAICA_PREFIX.length);
  }
  return value;
};

/**
 * Resolve a category slug from a clean URL to its query parameter.
 * The slug is always the plain value; the has_all: prefix is added for judaica.
 * e.g. resolveSlug('judaica', 'mezuzas') → { param: 'pub_category_j', value: 'has_all:mezuzas' }
 */
export const resolveSlug = (listingType, categorySlug) => {
  const param = paramByListingType[listingType];
  if (!param || !categorySlug) return null;

  const value = listingType === 'judaica' ? `${JUDAICA_PREFIX}${categorySlug}` : categorySlug;
  return { param, value };
};

/**
 * Find the clean URL slug for a given query parameter value.
 * Accepts both prefixed ("has_all:mezuzas") and plain ("mezuzas") values.
 * Always returns the plain slug for the URL.
 * e.g. findSlug('judaica', 'pub_category_j', 'has_all:mezuzas') → 'mezuzas'
 */
export const findSlug = (listingType, paramName, paramValue) => {
  const expectedParam = paramByListingType[listingType];
  if (!expectedParam || paramName !== expectedParam || !paramValue) return null;

  return toPlainValue(listingType, paramValue);
};

/**
 * Build a clean category path: /s/:listingType/:category
 */
export const categoryPath = (listingType, slug) => `/s/${listingType}/${slug}`;

/**
 * Resolve a collection slug from a clean URL to its query parameter.
 * /s/collection/:slug → { param: 'pub_collection', value: 'has_all:slug' }
 * e.g. resolveCollectionSlug('signs') → { param: 'pub_collection', value: 'has_all:signs' }
 */
export const resolveCollectionSlug = collectionSlug => {
  if (!collectionSlug) return null;
  return { param: 'pub_collection', value: `has_all:${collectionSlug}` };
};

/**
 * Build a clean collection path: /s/collection/:slug
 */
export const collectionPath = slug => `/s/collection/${slug}`;
